/**
 * 動画スライド生成ボタン (CompletionView から呼ばれる)。
 *
 * 状態:
 *   - idle / generating / done / error
 *
 * 各状態の描画は StatePanel サブコンポーネントに分割している (lint 行数制約のため)。
 */

import { Download, Film, RotateCcw } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';

import { buildVideoFromZip } from '../../lib/video/video-pipeline';
import { Button } from '../ui/button';

type State =
  | { readonly kind: 'idle' }
  | { readonly kind: 'generating'; readonly ratio: number; readonly message: string }
  | {
      readonly kind: 'done';
      readonly blob: Blob;
      readonly url: string;
      readonly name: string;
      readonly sizeMb: string;
      readonly durationSec: number;
    }
  | { readonly kind: 'error'; readonly message: string };

export interface VideoExportButtonProps {
  readonly zipBlob: Blob;
  /** ZIP と同じ命名で拡張子だけ .mp4 に置換したもの */
  readonly mp4Name: string;
}

export function VideoExportButton(props: VideoExportButtonProps): JSX.Element {
  const [state, setState] = useState<State>({ kind: 'idle' });

  useEffect(() => {
    if (state.kind !== 'done') return;
    return (): void => URL.revokeObjectURL(state.url);
  }, [state]);

  const start = useCallback(async () => {
    setState({ kind: 'generating', ratio: 0, message: '準備中' });
    try {
      const result = await buildVideoFromZip({
        zipBlob: props.zipBlob,
        outputName: props.mp4Name,
        onProgress: (p) =>
          setState({
            kind: 'generating',
            ratio: p.overallRatio,
            message: progressMessage(p.phase),
          }),
      });
      setState({
        kind: 'done',
        blob: result.blob,
        url: URL.createObjectURL(result.blob),
        name: props.mp4Name,
        sizeMb: (result.fileSizeBytes / (1024 * 1024)).toFixed(1),
        durationSec: Math.round(result.durationSec),
      });
    } catch (err) {
      setState({
        kind: 'error',
        message: err instanceof Error ? err.message : '不明なエラーが発生しました',
      });
    }
  }, [props.zipBlob, props.mp4Name]);

  const retry = useCallback(() => setState({ kind: 'idle' }), []);

  return <StatePanel state={state} onStart={start} onRetry={retry} />;
}

// ===== サブコンポーネント (lint 対策で分割) ================================

interface StatePanelProps {
  readonly state: State;
  readonly onStart: () => void;
  readonly onRetry: () => void;
}

function StatePanel(props: StatePanelProps): JSX.Element {
  const { state } = props;
  if (state.kind === 'idle') return <IdlePanel onStart={props.onStart} />;
  if (state.kind === 'generating')
    return <GeneratingPanel ratio={state.ratio} message={state.message} />;
  if (state.kind === 'done') return <DonePanel {...state} />;
  return <ErrorPanel message={state.message} onRetry={props.onRetry} />;
}

function IdlePanel({ onStart }: { readonly onStart: () => void }): JSX.Element {
  return (
    <div className="border-silver-tertiary flex flex-col gap-2 border-t pt-4">
      <div className="text-text-tertiary text-[10px] font-medium tracking-wider uppercase">
        動画スライド (カーセンサー物件動画用)
      </div>
      <Button variant="secondary" size="md" onClick={onStart} type="button">
        <Film className="mr-2 h-4 w-4" strokeWidth={1.5} aria-hidden />
        動画を生成 (4:3 MP4、約 2分40秒)
      </Button>
      <p className="text-text-tertiary text-[11px] leading-relaxed">
        ※ ffmpeg を初回のみダウンロード (約 30MB)。生成に数分かかります。
      </p>
    </div>
  );
}

function GeneratingPanel(props: { readonly ratio: number; readonly message: string }): JSX.Element {
  const pct = Math.round(props.ratio * 100);
  return (
    <div className="border-silver-tertiary flex flex-col gap-2 border-t pt-4">
      <div className="text-text-tertiary text-[10px] font-medium tracking-wider uppercase">
        動画生成中... {pct}%
      </div>
      <div className="bg-silver-tertiary h-2 w-full overflow-hidden rounded-full">
        <div
          className="bg-status-success h-full transition-all duration-300"
          style={{ width: `${pct}%` }}
        />
      </div>
      <p className="text-text-secondary text-xs">{props.message}</p>
    </div>
  );
}

function DonePanel(props: {
  readonly url: string;
  readonly name: string;
  readonly sizeMb: string;
  readonly durationSec: number;
}): JSX.Element {
  const mins = Math.floor(props.durationSec / 60);
  const secs = props.durationSec % 60;
  return (
    <div className="border-silver-tertiary flex flex-col gap-2 border-t pt-4">
      <div className="text-status-success text-[10px] font-medium tracking-wider uppercase">
        動画生成完了 — {props.sizeMb} MB / {mins}分{String(secs).padStart(2, '0')}秒
      </div>
      <Button asChild size="md" variant="secondary">
        <a href={props.url} download={props.name}>
          <Download className="mr-2 h-4 w-4" strokeWidth={1.5} aria-hidden />
          動画をダウンロード
        </a>
      </Button>
    </div>
  );
}

function ErrorPanel(props: {
  readonly message: string;
  readonly onRetry: () => void;
}): JSX.Element {
  return (
    <div className="border-silver-tertiary flex flex-col gap-2 border-t pt-4">
      <div className="text-status-danger text-[10px] font-medium tracking-wider uppercase">
        動画生成に失敗しました
      </div>
      <p className="text-text-secondary text-xs leading-relaxed">{props.message}</p>
      <Button variant="secondary" size="md" onClick={props.onRetry} type="button">
        <RotateCcw className="mr-2 h-4 w-4" strokeWidth={1.5} aria-hidden />
        やり直す
      </Button>
    </div>
  );
}

function progressMessage(phase: string): string {
  switch (phase) {
    case 'loading-ffmpeg':
      return 'ffmpeg を読み込み中';
    case 'writing-inputs':
      return '画像を準備中';
    case 'encoding':
      return 'エンコード中';
    case 'reading-output':
      return '仕上げ中';
    case 'done':
      return '完了';
    default:
      return phase;
  }
}
